// Generated by Claude Code
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { CompanyTagsManager, type CompanyTagsAdapter } from '#components/domain/operations/settings/shared/company-tags-settings.tsx'
import { CustomFieldsEditor, type CustomFieldsAdapter } from '#components/domain/operations/settings/shared/custom-fields-editor.tsx'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import VendorsApi from '#core/api/operations/Vendors.ts'
import { PermissionAction, PermissionService, VendorElevated } from '#core/entities/auth/roles/Permission.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/vendors')({
  component: RouteComponent,
})

const vendorsApi = new VendorsApi()

const vendorTagsAdapter: CompanyTagsAdapter = {
  list: () => vendorsApi.listVendorTags(),
  create: (payload) => vendorsApi.createVendorTag(payload.value, payload.description),
  update: (id, payload) => vendorsApi.updateVendorTag(id, payload),
  remove: async (id) => { await vendorsApi.deleteVendorTag(id) },
  // Persist a drag-reorder by writing each tag's new index to its `order` column.
  reorder: async (orderedIds) => {
    await Promise.all(orderedIds.map((id, index) => vendorsApi.updateVendorTag(id, { order: index })))
  },
}

const customFieldsAdapter: CustomFieldsAdapter = {
  load: () => vendorsApi.getVendorCustomPropertiesDefinition(),
  save: (definition) => vendorsApi.upsertVendorCustomPropertiesDefinition(definition),
}

/**
 * Vendor field keys in the order they appear on the New Vendor drawer. Keys match
 * `CreateVendorSchema` property names, except the virtual UI fields (`tags`,
 * `profile_picture`), whose `required` flag is enforced client-side only.
 */
const VENDOR_FIELD_ORDER = [
  'name',
  'description',
  'website',
  'identifier',
  'tags',
  'profile_picture',
  'related_parts',
  'related_assets',
] as const

const VENDOR_ALWAYS_VISIBLE = ['name']
const VENDOR_ALWAYS_REQUIRED = ['name']

type VendorsSettingsTab = 'form' | 'tags' | 'custom-fields'

/** Settings page for Vendors: vendor form, tag catalogue and custom field definitions. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const { t: tVendors } = useTranslation('vendors')
  const [activeTab, setActiveTab] = useState<VendorsSettingsTab>('form')
  const userState = useUserState()
  const { showClose, handleClose } = useQuickSettingsReturn()

  // Per-tab permission gating: a user only sees the tabs whose write bit they hold.
  const canEditForm = userState.hasPermission(PermissionService.VENDORS, PermissionAction.FORM_SETTINGS)
  const canManageTags = userState.hasPermission(PermissionService.VENDORS, VendorElevated.MANAGE_TAGS)
  const canManage = userState.hasPermission(PermissionService.VENDORS, PermissionAction.CUSTOM_PROPERTIES)

  const tabs = [
    canEditForm && { key: 'form' as const, label: tVendors('settings.tabForm') },
    canManageTags && { key: 'tags' as const, label: t('shared.fields.tags') },
    canManage && { key: 'custom-fields' as const, label: t('shared.fields.customFields') },
  ].filter(Boolean) as { key: VendorsSettingsTab; label: string }[]

  // Fall back to the first permitted tab when the active one is hidden (e.g. a deep link).
  const effectiveTab = tabs.some((tb) => tb.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    name: t('shared.fields.name'),
    description: t('shared.fields.description'),
    website: tVendors('columns.website'),
    identifier: t('shared.fields.customId'),
    tags: t('shared.fields.tags'),
    profile_picture: tVendors('settings.fieldPictureUpload'),
    related_parts: t('shared.fields.relatedParts'),
    related_assets: t('shared.fields.relatedAssets'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">{tVendors('settings.title')}</h1>
        </div>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as VendorsSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {effectiveTab === 'form' && (
            <div>
              <p className="text-secondary mb-4">{tVendors('settings.formDescription')}</p>
              <FormSettingsEditor
                entityType="vendor"
                fieldLabels={fieldLabels}
                orderedFields={[...VENDOR_FIELD_ORDER]}
                alwaysVisibleFields={VENDOR_ALWAYS_VISIBLE}
                alwaysRequiredFields={VENDOR_ALWAYS_REQUIRED}
                visibleColumn={{
                  label: tVendors('settings.columnVisible'),
                  helpLabel: tVendors('settings.columnVisibleHelpLabel'),
                  help: tVendors('settings.columnVisibleHelp'),
                }}
                requiredColumn={{
                  label: tVendors('settings.columnMandatory'),
                  helpLabel: tVendors('settings.columnMandatoryHelpLabel'),
                  help: tVendors('settings.columnMandatoryHelp'),
                }}
                canEdit={canEditForm}
              />
            </div>
          )}

          {effectiveTab === 'tags' && <CompanyTagsManager adapter={vendorTagsAdapter} canManage={canManageTags} />}

          {effectiveTab === 'custom-fields' && <CustomFieldsEditor adapter={customFieldsAdapter} canManage={canManage} />}
        </>
      )}
    </div>
  )
}
